/**
 * La connexion poussée, côté navigateur.
 *
 * Le serveur envoie un signal — « le canal notification a bougé » — et jamais
 * la donnée elle-même. Ce crochet ne fait donc qu'une chose : invalider le
 * cache du canal concerné, et laisser TanStack Query relire par la route
 * authentifiée habituelle (CCT § 3.3).
 *
 * Une seule connexion pour tous les canaux : la file d'attente de M1 et le
 * stock de M2 arriveront par le même socket.
 */
import { useEffect } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import {
  CANAUX_TEMPS_REEL,
  schemaMessageTempsReel,
  SOUS_PROTOCOLE_JETON,
  type CanalTempsReel,
} from '@afs/shared'
import {
  jetonAccesCourant,
  jetonAPerimer,
  renouvelerJetonAcces,
  signalerSessionFermee,
  type Renouvellement,
} from '../api/client.js'
import { noterTempsReel } from '../../modules/hors-ligne/etatReseau.js'
import { clesNotifications } from './api.js'
import { adresseTempsReel, delaiDeReconnexion, doitSeReconnecter } from './presentation.js'

/** Ce qu'un canal invalide quand il signale un changement : une ou plusieurs racines de cache. */
export const CLES_PAR_CANAL: Readonly<
  Record<CanalTempsReel, () => readonly (readonly unknown[])[]>
> = {
  // Une notification « écart d'offre » prévient aussi la chargée rattachée à un
  // autre site que le comptoir : sa liste des écarts se relit avec la cloche.
  // Doit rester le préfixe de `clesEcartOffre.ouverts`.
  // La réponse à un signalement rallume aussi le compteur de « Mes signalements »
  // (décision 177). Doit rester `clesMesSignalements.tout`.
  notification: () => [
    clesNotifications.tout,
    ['parcours-patient', 'signalements-offre'],
    ['mes-signalements'],
  ],
  // La clé est écrite ici et non importée du module : `shared` ne dépend
  // d'aucun module métier. Elle doit rester celle de `clesFile.tout`.
  file_attente: () => [['file-attente']],
  // Le statut d'une entreprise a changé, ou la chargée a confirmé ou infirmé
  // une fiche : la fiche ouverte et la recherche relisent l'éligibilité, la
  // caisse et le règlement de l'entrée relisent la situation (R5). Doivent
  // rester `clesParcours.tout` et `clesCaisse.tout`.
  eligibilite: () => [['parcours-patient'], ['caisse']],
  // La file « À confirmer » a bougé (réunion du 15/10) : la file et le
  // compteur du menu, mais aussi la fiche et la recherche de l'accueil — une
  // fiche confirmée devient « Éligible », et l'accueil qui la regarde ne doit
  // pas annoncer un paiement immédiat à un patient couvert (test S71, G1).
  // Doit rester `clesParcours.tout`, dont `clesParcours.aConfirmer` descend.
  a_confirmer: () => [['parcours-patient']],
  // Un reçu encaissé, annulé, une caisse clôturée (spécification du 16/10) :
  // la caisse, l'accueil et la vue de la Direction relisent. Doit rester
  // `clesCaisse.tout`. AJ-04 : une fiche à confirmer envoyée à la caisse sort de
  // « En attente de la chargée » — doit rester `clesAttenteChargee.liste()`.
  caisse: () => [['caisse'], ['parcours-patient', 'attente-chargee']],
}

/** Applique un message du serveur au cache. Exporté pour les tests. */
export function appliquer(client: QueryClient, donnees: unknown): void {
  // Un serveur plus récent que l'onglet ouvert peut annoncer un canal que
  // cette version ne connaît pas. On l'ignore poliment plutôt que de lever.
  const lu = schemaMessageTempsReel.safeParse(donnees)
  if (!lu.success) return

  for (const queryKey of CLES_PAR_CANAL[lu.data.canal]()) {
    void client.invalidateQueries({ queryKey })
  }
}

/**
 * Après une coupure, ce qui a bougé pendant qu'on n'écoutait pas n'a pas été
 * signalé : on relit tout ce que les canaux savent faire relire — file,
 * caisse, en attente de la chargée, à confirmer, vérifications, écarts,
 * notifications (décision 416). Les écrans montés relisent tout de suite, les
 * autres à leur prochaine ouverture. Une racine présente sur deux canaux
 * n'est invalidée qu'une fois : deux invalidations coup sur coup annuleraient
 * la première lecture pour la relancer.
 */
export function relireApresCoupure(client: QueryClient): void {
  const vues = new Set<string>()
  for (const canal of CANAUX_TEMPS_REEL) {
    for (const queryKey of CLES_PAR_CANAL[canal]()) {
      const empreinte = JSON.stringify(queryKey)
      if (vues.has(empreinte)) continue
      vues.add(empreinte)
      void client.invalidateQueries({ queryKey })
    }
  }
}

/** Ce dont la connexion a besoin — injecté, pour se tester sans navigateur. */
export interface DependancesTempsReel {
  readonly adresse: string
  readonly jeton: () => string | null
  /** Le jeton est-il périmé, ou près de l'être ? Seulement alors on le renouvelle. */
  readonly jetonAPerimer: () => boolean
  readonly renouveler: () => Promise<Renouvellement>
  readonly creerSocket: (adresse: string, protocoles: string[]) => WebSocket
  readonly surMessage: (donnees: unknown) => void
  /** La connexion revient après une coupure : relire ce qui a pu être manqué. */
  readonly surRetour: () => void
  /** Pour le bandeau : ouverte, coupée (on retente), ou arrêtée pour de bon. */
  readonly surEtat: (etat: 'ouverte' | 'coupee' | 'arretee') => void
  /** Le serveur a fermé la session (4001), avec son motif : renvoyer à la connexion. */
  readonly surSessionFermee: (motif: string) => void
}

export interface ConnexionTempsReel {
  /** Retente tout de suite au lieu d'attendre la fin du délai (le réseau est revenu). */
  readonly relancer: () => void
  readonly arreter: () => void
}

/**
 * La connexion et sa reconnexion, hors de React.
 *
 * Avant une nouvelle tentative, le jeton est renouvelé s'il est périmé ou
 * près de l'être (décision 416) : il vit 15 minutes, et une coupure plus
 * longue le laissait périmé — la bascule
 * était refusée en boucle jusqu'au premier clic, et le médecin perdait sa
 * présence, donc son box. Un refus du renouvellement veut dire « session
 * close » : on s'arrête (le client HTTP prévient l'application, qui renvoie
 * à la connexion). Un serveur injoignable : on retentera.
 */
export function connecterTempsReel(dep: DependancesTempsReel): ConnexionTempsReel {
  let socket: WebSocket | null = null
  let minuterie: ReturnType<typeof setTimeout> | null = null
  let tentative = 0
  let dejaOuverte = false
  let arretee = false
  // Lue après un `await` : TypeScript croirait sinon la valeur figée depuis le test d'entrée.
  const estArretee = (): boolean => arretee

  const arreter = (): void => {
    arretee = true
    dep.surEtat('arretee')
  }

  const planifier = (): void => {
    tentative += 1
    minuterie = setTimeout(() => void ouvrir(), delaiDeReconnexion(tentative))
  }

  const ouvrir = async (): Promise<void> => {
    minuterie = null
    if (arretee) return

    if (tentative > 0 && dep.jetonAPerimer()) {
      const issue = await dep.renouveler()
      if (estArretee()) return
      if (issue === 'refuse') {
        arreter()
        return
      }
      if (issue === 'injoignable') {
        planifier()
        return
      }
    }

    // Le jeton vit en mémoire et tourne. S'il manque à cet instant, le
    // client HTTP est en train de le renouveler : on retente plus tard
    // plutôt que d'ouvrir une connexion qui serait refusée.
    const jeton = dep.jeton()
    if (jeton === null) {
      planifier()
      return
    }

    // Un navigateur ne peut poser aucun en-tête sur un WebSocket : le
    // sous-protocole est le seul canal, hors de l'URL, où le jeton
    // n'atterrira pas dans les journaux d'accès du reverse proxy.
    const ouverte = dep.creerSocket(dep.adresse, [SOUS_PROTOCOLE_JETON, jeton])
    socket = ouverte

    ouverte.addEventListener('open', () => {
      const retour = dejaOuverte || tentative > 0
      tentative = 0
      dejaOuverte = true
      dep.surEtat('ouverte')
      if (retour) dep.surRetour()
    })

    ouverte.addEventListener('message', (evenement: MessageEvent<string>) => {
      try {
        dep.surMessage(JSON.parse(evenement.data))
      } catch {
        // Un message illisible n'est pas une raison de fermer la
        // connexion : le suivant sera peut-être bon.
      }
    })

    ouverte.addEventListener('close', (evenement: CloseEvent) => {
      if (socket === ouverte) socket = null
      if (arretee) return
      if (!doitSeReconnecter(evenement.code)) {
        arreter()
        dep.surSessionFermee(evenement.reason)
        return
      }
      dep.surEtat('coupee')
      planifier()
    })
  }

  void ouvrir()

  return {
    relancer: () => {
      if (arretee || minuterie === null) return
      clearTimeout(minuterie)
      void ouvrir()
    },
    arreter: () => {
      arreter()
      if (minuterie !== null) clearTimeout(minuterie)
      socket?.close()
    },
  }
}

/**
 * Ouvre la connexion tant que `actif`, et la referme en partant.
 *
 * `actif` est faux avant l'ouverture de session : sans jeton, la bascule
 * serait refusée et la boucle de reconnexion tournerait pour rien.
 */
export function useTempsReel(actif: boolean): void {
  const client = useQueryClient()

  useEffect(() => {
    if (!actif) return

    const connexion = connecterTempsReel({
      adresse: adresseTempsReel(window.location.origin),
      jeton: jetonAccesCourant,
      jetonAPerimer,
      renouveler: renouvelerJetonAcces,
      creerSocket: (adresse, protocoles) => new WebSocket(adresse, protocoles),
      surMessage: (donnees) => {
        appliquer(client, donnees)
      },
      surRetour: () => {
        relireApresCoupure(client)
      },
      surEtat: noterTempsReel,
      surSessionFermee: signalerSessionFermee,
    })
    // Le poste retrouve son lien : inutile d'attendre jusqu'à 30 s.
    const relancer = (): void => {
      connexion.relancer()
    }
    window.addEventListener('online', relancer)

    return () => {
      window.removeEventListener('online', relancer)
      connexion.arreter()
    }
  }, [actif, client])
}
